import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Sparkles, RotateCcw, BookOpen } from "lucide-react";
import Markdown from "../components/Markdown";

const SUGGESTED = [
  "What's the two-bounce rule?",
  "Best paddle for a 3.5 player who wants more control?",
  "How do I hit a reliable 3rd-shot drop?",
  "Kitchen (non-volley zone) rules explained simply?",
  "What's the difference between DUPR and UTR-P?",
  "Indoor vs outdoor balls — when to use which?",
  "How should I warm up before a tournament?",
  "Stacking: when does it make sense?",
];

const STORAGE_KEY = "pickleai:conversation";

export default function Ask() {
  const [messages, setMessages] = useState([]); // {role: "user"|"assistant", content}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  const messagesContainerRef = useRef();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setMessages(JSON.parse(raw));
    } catch (_) {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch (_) {}
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && messagesContainerRef.current) {
      // Scroll so the latest assistant bubble starts at the top
      const bubble = messagesContainerRef.current.querySelector(`[data-testid="ask-message-${messages.length - 1}-assistant"]`);
      if (bubble) {
        bubble.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    const history = [...messages];
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q, history });
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: data.answer,
        grounded: !!data.grounded,
        sources: data.sources || [],
      }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "PickleAI is unavailable");
      setMessages((prev) => [...prev, { role: "assistant", content: "*Sorry — couldn't reach PickleAI. Please try again.*" }]);
    }
    setLoading(false);
  };

  const reset = () => {
    if (!window.confirm("Clear this conversation?")) return;
    setMessages([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  return (
    <div data-testid="ask-page" className="space-y-6 h-[calc(100vh-180px)] flex flex-col">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Pickle AI</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter mt-1">Ask anything</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Rules, strategy, gear, drills — PickleAI has you covered.</p>
        </div>
        {messages.length > 0 && (
          <button onClick={reset} className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="ask-reset-button">
            <RotateCcw size={12} /> New chat
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="card-surface flex-1 overflow-hidden flex flex-col">
        <div ref={messagesContainerRef} className="flex-1 overflow-auto p-6 space-y-4" data-testid="ask-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="ask-empty-state">
              <div className="w-14 h-14 border border-[#38BDF8]/50 grid place-items-center mb-4">
                <Sparkles size={20} className="text-[#38BDF8]" aria-hidden="true" />
              </div>
              <div className="font-display text-2xl font-black uppercase tracking-tight">Your pocket pickleball expert</div>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-md">
                Ask about rules, equipment, strategy, drills, or anything pickleball. Try one of these to start:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6 max-w-2xl w-full">
                {SUGGESTED.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => ask(s)}
                    className="text-left text-sm p-3 border border-white/10 hover:border-[#38BDF8] hover:bg-[#38BDF8]/5 transition-colors"
                    data-testid={`ask-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] p-4 text-sm ${
                  m.role === "user"
                    ? "bg-[#38BDF8] text-black"
                    : "bg-white/5 text-white border border-white/10"
                }`}
                data-testid={`ask-message-${i}-${m.role}`}
              >
                {m.role === "user" ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : (
                  <>
                    {m.grounded && (
                      <div
                        className="mb-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#38BDF8]"
                        data-testid={`ask-message-${i}-grounded-badge`}
                      >
                        <BookOpen size={11} aria-hidden="true" />
                        Grounded in 2026 USA Pickleball Rulebook
                      </div>
                    )}
                    <Markdown>{m.content}</Markdown>
                  </>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start" data-testid="ask-typing">
              <div className="bg-white/5 border border-white/10 p-4 text-sm inline-flex items-center gap-2">
                <Sparkles size={14} className="text-[#38BDF8] animate-pulse" aria-hidden="true" />
                <span className="text-[#A3A3A3]">PickleAI is thinking…</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="p-3 border-t border-white/10 flex gap-2"
        >
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask PickleAI anything… (Enter to send, Shift+Enter for newline)"
            className="flex-1 bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none px-3 py-2 text-sm resize-none"
            data-testid="ask-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="btn-primary px-4 py-2 text-xs disabled:opacity-50"
            data-testid="ask-submit"
          >
            <Send size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
